import React, { useCallback, useMemo } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Stack } from "@mui/material";
import { format } from "date-fns";

import { TaskV3_1 } from "@fartherfinance/frontend/api/Tasks/Types";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import { useTasksNavigation } from "@src/multiCustodian/pages/Advisor/Tasks/hooks";
import TaskStatusChip from "@src/multiCustodian/pages/Advisor/Tasks/TaskStatusChip/TaskStatusChip";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import { Table } from "@src/sharedComponents/TableV2/Table";
import type { TableSortOrder } from "@src/sharedComponents/TableV2/TableHeader";
import { flipTableSortOrder } from "@src/sharedComponents/TableV2/TableHeader.utils";
import Text from "@src/sharedComponents/Typography/Text";

import TransitionTasksTablePagination from "./TransitionTasksTablePagination";
import { SortOrder, TransitionTasksTableProps } from "./types";

import styles from "./TransitionTasksTable.module.css";

const COLUMN_WIDTHS = ["2fr", "120px", "140px", "2fr"];
const DEFAULT_STORAGE_KEY = "farther:layout:transition-tasks-column-widths";
const SKELETON_ROWS_COUNT = 10;

const formatDate = (date: Date | null): string => {
  if (!date) {
    return "-";
  }

  return format(date, "MM/dd/yyyy");
};

const SkeletonRow: React.FC = () => (
  <Table.Row>
    <Table.Cell>
      <Skeleton width="80%" />
    </Table.Cell>
    <Table.Cell>
      <Skeleton width="60px" height="24px" sx={{ borderRadius: "6px" }} />
    </Table.Cell>
    <Table.Cell>
      <Skeleton width="50px" />
    </Table.Cell>
    <Table.Cell>
      <Skeleton width="90%" />
    </Table.Cell>
  </Table.Row>
);

const TableHeader: React.FC<{
  readonly title: string;
  readonly columnIndex: number;
  readonly sortable?: boolean;
  readonly active?: boolean;
  readonly sortOrder?: SortOrder;
  readonly onSort?: () => void;
}> = ({
  title,
  columnIndex,
  sortable = false,
  active = false,
  sortOrder,
  onSort,
}) => (
  <Table.Header
    className={styles.headerCell}
    columnIndex={columnIndex}
    sortable={sortable}
    active={active}
    sortOrder={sortOrder}
    onClick={onSort}
  >
    {title}
  </Table.Header>
);

const TaskRow: React.FC<{
  readonly task: TaskV3_1;
  readonly onTaskClick: (task: TaskV3_1) => void;
}> = ({ task, onTaskClick }) => (
  <Table.Row className={styles.dataRow} onClick={() => onTaskClick(task)}>
    <Table.Cell className={styles.dataCell}>
      <Text variant="body" color="text">
        {task.title}
      </Text>
    </Table.Cell>

    <Table.Cell className={styles.dataCell}>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <TaskStatusChip taskStatus={task.taskStatus} />
      </div>
    </Table.Cell>

    <Table.Cell className={styles.dataCell}>
      <div style={{ textAlign: "right" }}>
        <Text variant="body" color="text">
          {formatDate(task.dueDate)}
        </Text>
      </div>
    </Table.Cell>

    <Table.Cell className={styles.dataCell}>
      <Text variant="body" color="textSubtle">
        {task.description || "-"}
      </Text>
    </Table.Cell>
  </Table.Row>
);

const EmptyState: React.FC = () => (
  <Table.Row className={styles.noTasks}>
    <Table.Cell>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        style={{
          justifyContent: "center",
          marginBottom: "15px",
        }}
      >
        <ErrorOutlineIcon className={styles.icon} />
        <Text>No tasks available</Text>
      </Stack>
    </Table.Cell>
  </Table.Row>
);

const ErrorState: React.FC<{ message: string; onRetry?: () => void }> = ({
  message,
  onRetry,
}) => (
  <Table.Row className={styles.noTasks}>
    <Table.Cell>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        style={{
          justifyContent: "center",
          marginBottom: "15px",
        }}
      >
        <ErrorOutlineIcon className={styles.icon} />
        <Text>{message}</Text>
      </Stack>
      {onRetry && (
        <Button
          text="Refresh"
          size="small"
          variant="outlined"
          buttonType="primary"
          onClick={onRetry}
          endIcon={<RefreshIcon className={styles.icon} />}
        />
      )}
    </Table.Cell>
  </Table.Row>
);

const TransitionTasksTable: React.FC<TransitionTasksTableProps> = ({
  tasks,
  loading = false,
  error,
  pagination,
  sorting,
  onPageChange,
  onSort,
  onRetry,
  storageKey = DEFAULT_STORAGE_KEY,
}) => {
  const { handleNavigateToTaskDetails } = useTasksNavigation();

  const handleSort = useCallback(
    (column: string) => {
      const newOrder: TableSortOrder =
        sorting.sortBy === column
          ? flipTableSortOrder(sorting.sortOrder as TableSortOrder)
          : "asc";
      onSort(column, newOrder);
    },
    [sorting.sortBy, sorting.sortOrder, onSort]
  );

  const handleTaskClick = useCallback(
    (task: TaskV3_1) => {
      handleNavigateToTaskDetails(task.id);
    },
    [handleNavigateToTaskDetails]
  );

  const handleDueDateSort = useCallback(() => {
    handleSort("dueDate");
  }, [handleSort]);

  const headerRow = useMemo(
    () => (
      <Table.Row className={styles.headerRow}>
        <TableHeader title="Task" columnIndex={0} />
        <TableHeader title="Status" columnIndex={1} />
        <TableHeader
          title="Due Date"
          columnIndex={2}
          sortable
          active={sorting.sortBy === "dueDate"}
          sortOrder={sorting.sortOrder}
          onSort={handleDueDateSort}
        />
        <TableHeader title="Description" columnIndex={3} />
      </Table.Row>
    ),
    [sorting.sortBy, sorting.sortOrder, handleDueDateSort]
  );

  const tableContent = useMemo(() => {
    if (loading) {
      return Array.from({ length: SKELETON_ROWS_COUNT }, (_, index) => (
        <SkeletonRow key={`skeleton-${index}`} />
      ));
    }

    if (error) {
      return <ErrorState message={error.message} onRetry={onRetry} />;
    }

    if (!tasks.length) {
      return <EmptyState />;
    }

    return tasks.map((task) => (
      <TaskRow key={task.id} task={task} onTaskClick={handleTaskClick} />
    ));
  }, [loading, error, tasks, handleTaskClick, onRetry]);

  return (
    <div className={styles.tableContainer}>
      <Table
        initialColumnWidths={COLUMN_WIDTHS}
        externalStorage={localStorage}
        externalStorageKey={storageKey}
        resizable={false}
      >
        {headerRow}
        {tableContent}
      </Table>

      <TransitionTasksTablePagination
        currentPage={pagination.currentPage}
        pageSize={pagination.pageSize}
        totalCount={pagination.totalCount}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default TransitionTasksTable;
